import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    document.addEventListener('click', this.#onBodyClick);

    // Ensure summary clicks go through our JS methods rather than instantly native-toggling,
    // which breaks the close animations and focus trap resets.
    const summary = this.refs.details?.querySelector('summary');
    if (summary) {
      summary.addEventListener('click', this.#onSummaryClick);
    }

  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    document.removeEventListener('click', this.#onBodyClick);
    const summary = this.refs.details?.querySelector('summary');
    if (summary) {
      summary.removeEventListener('click', this.#onSummaryClick);
    }
  }

  #onSummaryClick = (event) => {
    event.preventDefault();
    if (event.target.closest('.drawer-back-to-solutions')) {
      return;
    }
    if (this.isOpen) {
      this.close();
    } else {
      this.refs.details.setAttribute('open', '');
      this.open();
    }
  };

  /**
   * Close the main menu drawer when clicking outside
   * @param {MouseEvent} event
   */
  #onBodyClick = (event) => {
    if (!this.isOpen) return;

    // Explicitly handle backdrop clicks
    if (event.target.classList.contains('menu-drawer__backdrop') || event.target.closest('.menu-drawer__backdrop')) {
      this.close();
      return;
    }

    const isInsideMenuDrawer = event.target.closest('.menu-drawer');
    const isSummary = event.target.closest('summary');

    // On desktop, the menu-drawer wrapper spans the entire screen. 
    // If the click lands on the transparent wrapper itself (not the content panels), treat it as an outside click.
    if (isInsideMenuDrawer) {
      const isTransparentWrapper = event.target.classList.contains('menu-drawer') || 
                                   event.target.tagName.toLowerCase() === 'custom-mega-menu-drawer' ||
                                   event.target.classList.contains('menu-drawer__navigation');
                                   
      if (window.innerWidth >= 990 && isTransparentWrapper) {
        this.close();
        return;
      }
      
      return;
    }

    if (isSummary) {
      return;
    }

    this.close();
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        details.classList.add('menu-open');

        // Wait for the drawer animation to complete before trapping focus
        const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
        onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
      });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    let animationEnded = false;
    onAnimationEnd(
      drawer || details,
      () => {
        animationEnded = true;
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );

    // Fallback: If there is no CSS transition (e.g. on desktop), force the reset
    setTimeout(() => {
      if (!animationEnded) {
        animationEnded = true;
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      }
    }, 250);
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
